import React, {useEffect, useMemo, useRef, useState} from "react";
import {Sidebar} from "primereact/sidebar";
import {InputText} from "primereact/inputtext";
import {Button} from "primereact/button";
import {Avatar} from "primereact/avatar";
import {Checkbox} from "primereact/checkbox";
import {ProgressSpinner} from "primereact/progressspinner";
import {IconField} from "primereact/iconfield";
import {InputIcon} from "primereact/inputicon";
import {useLocation, useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import api from "../../../system/api";
import Icon from "../../../system/components/icon";
import {useTranslate} from "../../../system/useTranslate";
import {helpChecklistItems, helpPostDescription} from "./help-sidebar-utils";

function HelpSidebar({visible, setVisible, pageConfig}) {
    const translate = useTranslate();
    const navigate = useNavigate();
    const location = useLocation();
    const {config} = useSelector(state => state.app);

    const helpHubUrl = config?.["help-hub-url"] || "https://help.zofino.ir";

    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [search, setSearch] = useState("");
    const [checkedChecklistItems, setCheckedChecklistItems] = useState({});

    // Published help-center content; posts of the old section were copied into it with the same ids.
    const getPostsRef = useRef(async () => {
        const {data} = await api.get("/help-center/content");
        const content = data?.data || {};
        return [...(content.articles || []), ...(content.videos || [])].map((item) => ({
            id: item.id,
            documentId: item.id,
            type: item.type,
            title: item.title,
            description: item.summary,
            icon: item.type === "video" ? "play_circle" : "article",
        }));
    });

    useEffect(() => {
        if (!visible || loaded) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        getPostsRef.current(["published"])
            .then((data) => {
                if (cancelled) return;
                setPosts(data || []);
                setLoaded(true);
            })
            .catch((requestError) => {
                if (cancelled) return;
                setError(requestError);
            })
            .finally(() => {
                if (cancelled) return;
                setLoading(false);
            });
        return () => { cancelled = true; };
    }, [visible, loaded]);

    const articles = useMemo(() => {
        const pageUrl = String(pageConfig?.["nav-url"] || "").trim();
        if (!pageUrl) return [];
        return posts.filter((post) => {
            const assignedPages = Array.isArray(post.helpPages)
                ? post.helpPages
                : post.helpPage ? [post.helpPage] : [];
            return assignedPages.some((assignedPage) => String(assignedPage || "").trim() === pageUrl);
        });
    }, [posts, pageConfig]);

    const checklist = useMemo(() => {
        return helpChecklistItems(pageConfig?.helps?.checklistIds).map((text, index) => ({
            key: `${pageConfig?.["nav-url"] || "page"}-${index}-${text}`,
            text,
        }));
    }, [pageConfig]);

    const matchesSearch = (post) => {
        const term = search.trim().toLowerCase();
        if (!term) return true;
        return `${post.title || ""} ${helpPostDescription(post)}`.toLowerCase().includes(term);
    };

    const visibleArticles = articles.filter(matchesSearch);
    const visibleChecklist = checklist.filter((item) => (
        item.text.toLowerCase().includes(search.trim().toLowerCase())
    ));

    const handleNavigate = (post) => {
        const id = post.documentId || post.id;
        if (!id) return;
        setVisible?.(false);
        navigate(`/help?view=${post.type === "video" ? "video" : "article"}&id=${encodeURIComponent(id)}`, {state: {from: location.pathname}});
    };

    const handleRetry = () => {
        setLoaded(false);
        setPosts([]);
        setError(null);
    };

    const renderHelpItem = (post) => {
        const description = helpPostDescription(post);
        return (
        <Button
            type="button"
            text
            severity="secondary"
            key={post.documentId || post.id}
            className="w-full align-items-start justify-content-start gap-2 p-2 surface-ground border-round text-right"
            onClick={() => handleNavigate(post)}
        >
            <Avatar
                size="small"
                icon={<Icon icon={`mi mi:${post.icon || "article"}`} style={{color: "#0CC0CC"}}/>}
                className="flex-none"
            />
            <div className="flex flex-column min-w-0 flex-1">
                <span className="font-bold text-xs line-height-3">{post.title}</span>
                {description && (
                    <span className="text-2xs text-500 line-height-3">{description}</span>
                )}
            </div>
            <Icon icon="mi mi:chevron_left" className="text-base text-400 flex-none mt-1"/>
        </Button>
        );
    };

    const renderArticle = (post) => renderHelpItem(post);
    const renderChecklistItem = (item) => {
        const inputId = `page-help-checklist-${item.key.replace(/[^a-zA-Z0-9-_]/g, "-")}`;
        const checked = Boolean(checkedChecklistItems[item.key]);

        return (
            <div key={item.key}
                className="flex align-items-center gap-2 p-2 surface-ground border-round">
                <Checkbox
                    inputId={inputId}
                    binary
                    checked={checked}
                    className="flex-none"
                    onChange={(event) => setCheckedChecklistItems((current) => ({
                        ...current,
                        [item.key]: event.checked,
                    }))}
                />
                <label
                    htmlFor={inputId}
                    className={`flex-1 text-xs line-height-3 cursor-pointer ${checked ? "text-400" : "text-700"}`}
                    style={{textDecoration: checked ? "line-through" : "none"}}
                >
                    {item.text}
                </label>
            </div>
        );
    };

    return (
        <Sidebar
            visible={visible}
            position="left"
            showCloseIcon={false}
            className="rtl w-20rem headerless-sidebar"
            onHide={() => setVisible?.(false)}
            pt={{
                root: () => ({
                    style: {width: "min(94vw, 22rem)", maxWidth: "100vw", border: 0},
                }),
                content: () => ({
                    style: {padding: 0, overflow: "hidden"},
                }),
            }}
        >
            <div className="flex flex-column h-full">
                <div className="flex align-items-center justify-content-between px-3 py-2 border-bottom-1 border-200">
                    <span className="font-bold text-xs">{translate("slide-over.title")}</span>
                    <Button
                        type="button"
                        text
                        className="w-2rem h-2rem p-0 flex-none"
                        icon={<Icon icon="mi mi:close" className="text-lg"/>}
                        aria-label={translate("close")}
                        onClick={() => setVisible?.(false)}
                    />
                </div>

                <div className="px-3 py-3 border-bottom-1 border-200">
                    <IconField className="flex align-items-center w-full">
                        <InputIcon className="pi pi-search text-400"/>
                        <InputText
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            className="w-full border-round-lg text-xs"
                            placeholder={translate("slide-over.search-placeholder")}
                        />
                    </IconField>
                </div>

                <div className="flex-1 overflow-auto py-3 px-3">
                    <section className="flex flex-column gap-3 mb-3">
                        <span className="text-2xs font-bold text-500 text-uppercase">
                            {translate("slide-over.related-to-page")}
                        </span>
                        {loading && (
                            <div className="flex align-items-center justify-content-center py-3">
                                <ProgressSpinner className="w-2rem h-2rem"/>
                            </div>
                        )}
                        {!loading && error && (
                            <div className="flex flex-column align-items-center gap-2 py-3">
                                <span className="text-xs text-red-500 text-center">
                                    {translate("mobile.common.load-error")}
                                </span>
                                <Button
                                    type="button"
                                    text
                                    className="text-xs gap-2"
                                    icon={<Icon icon="pi pi-refresh"/>}
                                    label={translate("retry")}
                                    onClick={handleRetry}
                                />
                            </div>
                        )}
                        {!loading && !error && visibleArticles.length === 0 && (
                            <span className="text-2xs text-400">{translate("slide-over.no-articles")}</span>
                        )}
                        {!loading && !error && visibleArticles.map(renderArticle)}
                    </section>

                    {visibleChecklist.length > 0 && (
                        <section className="flex flex-column gap-3">
                            <span className="text-2xs font-bold text-500 text-uppercase">
                                {translate("slide-over.checklist-title")}
                            </span>
                            {visibleChecklist.map(renderChecklistItem)}
                        </section>
                    )}
                </div>

                <div className="p-3 border-top-1 border-200 flex flex-column gap-3">
                    <Button
                        type="button"
                        className="w-full border-round-lg text-xs gap-2"
                        style={{backgroundColor: "#0CC0CC", borderColor: "#0CC0CC"}}
                        icon={<Icon icon="pi pi-graduation-cap" className="text-lg"/>}
                        label={translate("slide-over.view-all-tutorials")}
                        onClick={() => navigate("/help")}
                    />
                    <Button
                        type="button"
                        className="w-full border-round-lg text-xs gap-2"
                        style={{backgroundColor: "#E6511C", borderColor: "#E6511C"}}
                        icon={<Icon icon="pi pi-comments" className="text-lg"/>}
                        label={translate("slide-over.online-support")}
                        onClick={() => window.open(helpHubUrl, "_blank", "noopener,noreferrer")}
                    />
                </div>
            </div>
        </Sidebar>
    );
}

export default HelpSidebar;
